import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { uploadApi } from '../api';
import { useAuth } from '../context/AuthContext';

// Helper function to capitalize first letter of username (if it starts with a letter)
const capitalizeFirstLetter = (username) => {
  if (!username || typeof username !== 'string') return '';
  
  const firstChar = username.charAt(0);
  const isLetter = /^[a-zA-Z]$/.test(firstChar);
  
  if (isLetter) {
    return username.charAt(0).toUpperCase() + username.slice(1);
  } else {
    return username;
  }
};

function Profile() {
  const { user, refreshUser } = useAuth();

  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationInput, setLocationInput] = useState({ city: '', state: '' });
  const [savingLocation, setSavingLocation] = useState(false);
  // Avatar upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [hoverTimer, setHoverTimer] = useState(null);

  // Default avatar URL (generic placeholder)
  const defaultAvatarUrl = 'http://localhost:5000/uploads/avatars/default-avatar.png';

  useEffect(() => {
    console.log('📋 Profile page mounted, user:', user?.username || 'not logged in');
    
    if (user) {
      fetchMyListings();
      // Reset avatar preview state when user changes
      setAvatarPreview(user?.avatar || null);
      
      console.log('📍 User location data:', user.location);
      
      // Load location data from user profile
      if (user.location && (user.location.city || user.location.state)) {
        setLocationInput({
          city: user.location.city || '',
          state: user.location.state || ''
        });
        console.log('✅ Using existing location:', user.location);
        setLoading(false);
      } else {
        // Auto-detect location on first load for users without saved location
        console.log('🔍 No saved location, triggering auto-detection...');
        detectAndSaveLocation();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [user]);

  // Function to detect and save location automatically
  const detectAndSaveLocation = async () => {
    console.log('🔍 Starting automatic location detection...');
    
    try {
      setSavingLocation(true);
      
      // Method 1: Try browser geolocation API first (most accurate, works regardless of VPN)
      if (navigator.geolocation) {
        console.log('📍 Trying browser geolocation...');
        const browserResult = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              console.log('📍 Browser geolocation got:', latitude, longitude);
              
              // Reverse geocode via Nominatim
              try {
                const resp = await fetch(
                  `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                );
                if (resp.ok) {
                  const data = await resp.json();
                  const city = data.address?.city || data.address?.town || data.address?.village || 'Unknown';
                  const state = data.address?.state || 'Unknown';
                  console.log('✅ Browser geolocation result:', { city, state });
                  resolve({ city, state, source: 'browser' });
                  return;
                }
              } catch (e) {
                console.warn('Reverse geocoding failed:', e.message);
              }
              resolve(null);
            },
            (error) => {
              switch(error.code) {
                case error.PERMISSION_DENIED:
                  console.log('Browser geolocation denied');
                  break;
                case error.POSITION_UNAVAILABLE:
                  console.log('Browser geolocation unavailable');
                  break;
                case error.TIMEOUT:
                  console.log('Browser geolocation timed out');
                  break;
              }
              resolve(null);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
          );
        });
        
        if (browserResult) {
          await saveLocation(browserResult.city, browserResult.state);
          setLocationInput({ city: browserResult.city, state: browserResult.state });
          setSavingLocation(false);
          return;
        }
      }
      
      // Method 2: Fallback to IP geolocation
      console.log('🌐 Trying IP geolocation fallback...');
      let clientIP = null;
      try {
        const ipResp = await fetch('https://api.ipify.org?format=json');
        if (ipResp.ok) {
          const ipData = await ipResp.json();
          clientIP = ipData.ip;
          console.log('🌐 Your public IP:', clientIP);
        }
      } catch (e) {
        console.warn('Could not get public IP:', e.message);
      }
      
      if (clientIP) {
        const response = await api.post('/api/location/detect-ip', { clientIP });
        
        if (response.data.success && response.data.data) {
          console.log('📍 IP geolocation result:', response.data.data);
          setLocationInput({
            city: response.data.data.city,
            state: response.data.data.state
          });
          await saveLocation(response.data.data.city, response.data.data.state);
        } else {
          throw new Error(response.data.message || 'IP geolocation failed');
        }
      } else {
        throw new Error('Could not determine your public IP');
      }
    } catch (error) {
      console.error('❌ Geolocation error:', error);
      alert('Failed to detect location automatically: ' + error.message + '. Please enter it manually.');
      setSavingLocation(false);
    }
  };

  const saveLocation = async (city, state) => {
    try {
      setSavingLocation(true);
      
      console.log('💾 Saving location:', { city, state });
      const response = await api.post('/api/auth/location', { city, state });
      
      if (response.data.success) {
        // Update local state
        setLocationInput({ city: city.trim(), state: state.trim() });
        
        // Refresh user data from profile (await so UI updates before returning)
        await refreshUser();
        
        // Don't use alert - the page updates in-place to show the new location
      } else {
        console.error('Failed to save location:', response.data.message);
        alert('Failed to save location. Please try again.');
      }
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Failed to save location. Please try again.');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleManualLocationChange = (e) => {
    const { name, value } = e.target;
    setLocationInput(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchMyListings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/auth/listings');

      if (response.data.success) {
        setMyListings(response.data.data);
      } else {
        console.error('Failed to fetch listings:', response.data.message);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching listings:', error);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      } else if (error.response?.status === 404) {
        console.error('Endpoint not found:', error.config.url);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      alert('New password must be different from current password!');
      return;
    }

    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data.success) {
        alert('Password changed successfully!');
        setShowChangePassword(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMsg = error.response?.data?.message || 'Failed to change password. Please try again.';
      alert(errorMsg);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/gif', 'image/png', 'image/bmp'];
    if (!validTypes.includes(file.type)) {
      setAvatarError('Invalid file type. Please use JPG, JPEG, GIF, PNG, or BMP.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('File size must be less than 2MB.');
      return;
    }

    setUploadingAvatar(true);
    setAvatarError('');

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await uploadApi.post('/api/auth/avatar', formData);

      if (response.data.success) {
        alert('Avatar uploaded successfully!');
        setAvatarPreview(response.data.avatarUrl || user.avatar);
        
        try {
          const profileResponse = await api.get('/api/auth/profile');
          if (profileResponse.data.success) {
            localStorage.setItem('userData', JSON.stringify(profileResponse.data.data));
            window.location.reload();
          }
        } catch (refreshError) {
          console.error('Failed to refresh profile:', refreshError);
          setTimeout(() => window.location.reload(), 500);
        }
      } else {
        setAvatarError(response.data.message || 'Upload failed');
        alert(`Upload error: ${response.data.message}`);
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        window.location.href = '/login';
        alert('Session expired. Please log in again.');
        return;
      }

      const errorMsg = err.response?.data?.message || 'Failed to upload avatar';
      setAvatarError(errorMsg);
      console.error('Response data:', err.response?.data);
      alert(`Upload failed: ${errorMsg}`);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = () => {
    console.log('🔵 Remove avatar button clicked!');
    
    // Use setTimeout to ensure confirm dialog renders properly
    setTimeout(() => {
      // Simple confirm dialog with delay to prevent React timing issues
      const confirmed = window.confirm(
        'Are you sure you want to remove your profile picture?\n\n' +
        'The default avatar will be restored.'
      );
      
      console.log('Confirm result:', confirmed);

      if (!confirmed) {
        console.log('User cancelled removal');
        return;
      }

      console.log('Confirmed - now removing...');
      
      // Call the actual async removal logic
      performAvatarRemoval();
    }, 0);
    
    // Call the actual async removal logic
    performAvatarRemoval();
  };
  
  const performAvatarRemoval = async () => {
    
    try {
      const response = await api.delete('/api/auth/avatar');
      
      if (response.data.success) {
        alert('✅ Profile picture removed! The default avatar has been restored.');
        
        // Clear localStorage and refresh user data from server
        try {
          const profileResponse = await api.get('/api/auth/profile');
          if (profileResponse.data.success) {
            localStorage.setItem('userData', JSON.stringify(profileResponse.data.data));
            window.location.reload();
          } else {
            window.location.reload();
          }
        } catch (refreshError) {
          console.error('Failed to refresh profile after removal:', refreshError);
          window.location.reload();
        }
      } else {
        console.error('Delete returned false:', response.data.message);
        alert(`Failed to remove avatar: ${response.data.message}`);
      }
    } catch (err) {
      console.error('Avatar removal error:', err);
      
      if (err.response?.data) {
        console.error('Backend error:', err.response.data);
      }
      
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        window.location.href = '/login';
        alert('Session expired. Please log in again.');
        return;
      }

      const errorMsg = err.response?.data?.message || 'Failed to remove avatar';
      alert(`Failed to remove avatar: ${errorMsg}`);
    }
  };

  const toggleListingStatus = async (listingId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus === 'active' ? 'deactivate' : 'reactivate'} this listing?`)) {
      return;
    }

    try {
      const response = await api.put(`/api/listings/${listingId}/status`);
      if (response.data.success) {
        alert(`Listing ${currentStatus === 'active' ? 'deactivated' : 'reactivated'}!`);
        await fetchMyListings();
      } else {
        alert('Failed to update listing: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error updating listing:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update listing';
      alert('Failed to update listing: ' + errorMsg);
    }
  };

  const deleteListing = async (listingId, title) => {
    console.log('deleteListing called with:', listingId, title);
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone!`)) {
      return;
    }

    try {
      console.log('Sending DELETE to /api/listings/' + listingId);
      const response = await api.delete(`/api/listings/${listingId}`);
      console.log('DELETE response:', response.data);
      if (response.data.success) {
        await fetchMyListings();
      } else {
        alert('Failed to delete listing: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting listing:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to delete listing';
      alert('Failed to delete listing: ' + errorMsg);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAvatarUrl = () => {
    if (!user?.avatar) return null;
    if (user.avatar.startsWith('http')) return user.avatar;
    return `http://localhost:5000${user.avatar}`;
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Please log in to view your profile</h1>
        <Link to="/login" className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors">
          Go to Login
        </Link>
      </div>
    );
  }

  const displayUsername = capitalizeFirstLetter(user.username);
  const avatarUrl = getAvatarUrl();

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">{displayUsername}</h1>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            {/* Avatar container with click-to-open-menu */}
            <div className="relative group">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt="Profile avatar" 
                  className="w-20 h-20 rounded-full object-cover border-4 border-blue-500 shadow-lg cursor-pointer hover:brightness-[90%] transition-all duration-200"
                  onClick={() => setShowAvatarMenu(true)}
                />
              ) : (
                <img 
                  src={defaultAvatarUrl} 
                  alt="Default profile avatar" 
                  className="w-20 h-20 rounded-full object-cover border-4 border-gray-300 shadow-md cursor-pointer hover:brightness-[90%] transition-all duration-200"
                  onClick={() => setShowAvatarMenu(true)}
                />
              )}
              
              {/* Hover overlay - no text, just visual feedback */}
            </div>

            {/* Avatar error message */}
            {avatarError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                <p className="text-sm text-red-700">{avatarError}</p>
              </div>
            )}
          </div>

          {/* Create New Listing button on the right */}
          <div className="flex flex-col gap-2">
            <Link 
              to="/create-listing" 
              className="bg-primary text-white px-5 py-2 rounded-md hover:bg-blue-800 transition-colors shadow-md text-sm font-medium"
            >
              + Create New Listing
            </Link>

            {/* Placeholder button for future use */}
            <button 
              className="bg-gray-200 text-gray-600 px-5 py-2 rounded-md hover:bg-gray-300 transition-colors shadow-md text-sm font-medium cursor-not-allowed"
              disabled
            >
              Do Something Good
            </button>
          </div>
        </div>

        {/* Avatar action menu (modal) */}
        {showAvatarMenu && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAvatarMenu(false)}>
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4">Avatar Options</h3>
              
              <div className="space-y-3">
                {/* Upload option - only show if user doesn't have custom avatar */}
                {!user?.avatar && (
                  <label 
                    htmlFor="avatarMenuUpload"
                    className={`flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded cursor-pointer transition-colors ${uploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="text-sm font-medium">Upload Avatar</span>
                    <input 
                      type="file" 
                      id="avatarMenuUpload"
                      accept=".jpg,.jpeg,.gif,.png,.bmp"
                      onChange={handleAvatarUpload}
                      disabled={uploadingAvatar}
                      className="hidden"
                    />
                  </label>
                )}

                {/* Change option - only show if user has custom avatar */}
                {user?.avatar && (
                  <label 
                    htmlFor="avatarMenuChange"
                    className={`flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded cursor-pointer transition-colors ${uploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="text-sm font-medium">Change Avatar</span>
                    <input 
                      type="file" 
                      id="avatarMenuChange"
                      accept=".jpg,.jpeg,.gif,.png,.bmp"
                      onChange={handleAvatarUpload}
                      disabled={uploadingAvatar}
                      className="hidden"
                    />
                  </label>
                )}

                {/* Remove option - only show if user has custom avatar */}
                {user?.avatar && (
                  <button 
                    onClick={() => {
                      setShowAvatarMenu(false);
                      handleRemoveAvatar();
                    }}
                    className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 rounded cursor-pointer transition-colors text-sm font-medium text-red-700"
                  >
                    <span>Remove Avatar</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}

                {/* Cancel button */}
                <button 
                  onClick={() => setShowAvatarMenu(false)}
                  className="w-full p-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email and Location (single column) */}
        <div className="space-y-3">
          {user.email && (
            <p className="text-lg">{user.email}</p>
          )}
          
          {/* Location Display */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
            {user.location && user.location.city && user.location.state ? (
              <div
                className="relative"
                onMouseEnter={() => {
                  if (hoverTimer) clearTimeout(hoverTimer);
                  const timer = setTimeout(() => setShowUpdateButton(true), 1000);
                  setHoverTimer(timer);
                }}
                onMouseLeave={() => {
                  if (hoverTimer) clearTimeout(hoverTimer);
                  setShowUpdateButton(false);
                }}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm text-gray-700">
                    {user.location.city}, {user.location.state}
                  </p>
                </div>
                
                {/* Update button - only visible after 1s hover */}
                {showUpdateButton && (
                  <button
                    onClick={() => {
                      setShowUpdateButton(false);
                      setShowUpdateForm(true);
                    }}
                    className="mt-2 text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Update my location</span>
                  </button>
                )}
                
                {/* Manual entry form - only visible when update button is clicked */}
                {showUpdateForm && (
                  <div className="mt-2 flex items-center space-x-2">
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={locationInput.city}
                      onChange={handleManualLocationChange}
                      className="text-sm px-2 py-1 border rounded w-28"
                    />
                    <input
                      type="text"
                      name="state"
                      placeholder="State"
                      value={locationInput.state}
                      onChange={handleManualLocationChange}
                      className="text-sm px-2 py-1 border rounded w-20"
                    />
                    <button
                      onClick={async () => {
                        if (locationInput.city && locationInput.state) {
                          setSavingLocation(true);
                          await saveLocation(locationInput.city, locationInput.state);
                          setSavingLocation(false);
                          setShowUpdateForm(false);
                        }
                      }}
                      disabled={savingLocation || !locationInput.city || !locationInput.state}
                      className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      Update
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-sm text-gray-700">Location not set</p>
              </div>
            )}
            
            {/* Auto-detect button - only shown when no location exists */}
            {!user.location || (!user.location.city && !user.location.state) ? (
              <div className="mt-2 space-y-2">
                <button
                  onClick={detectAndSaveLocation}
                  disabled={savingLocation}
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors flex items-center space-x-1 disabled:opacity-50"
                >
                  {savingLocation ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.942z"></path>
                      </svg>
                      <span>Detecting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Auto-detect my location</span>
                    </>
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-6 flex space-x-4">
          {/* Navigation links moved to Account dropdown */}
        </div>
      </div>

      {/* My Listings Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">My Active Listings ({myListings.length})</h2>

        {loading ? (
          <p>Loading your listings...</p>
        ) : myListings.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v8" />
            </svg>
            <p className="text-gray-600 mb-4">You haven't created any listings yet.</p>
            <Link to="/create-listing" className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors">
              Create Your First Listing
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {myListings.map(listing => (
              <div key={listing._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  <Link to={`/listings/${listing._id}`} className="flex-shrink-0">
                    <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                      {listing.images && listing.images.length > 0 && listing.images[0].url ? (
                        <img
                          src={listing.images[0].thumbnailUrl || `http://localhost:5000${listing.images[0].url}`}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            if (!e.currentTarget.dataset.fallback) {
                              e.currentTarget.dataset.fallback = "true";
                              e.currentTarget.src = `http://localhost:5000${listing.images[0].url}`;
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <Link
                        to={`/listings/${listing._id}`}
                        className="text-xl font-semibold text-primary hover:underline truncate"
                      >
                        {listing.title}
                      </Link>

                      <span className={`px-3 py-1 rounded-full text-sm ${
                        listing.status === 'active' ? 'bg-green-100 text-green-800' :
                        listing.status === 'sold' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {listing.status}
                      </span>
                    </div>

                    <p className="text-secondary font-semibold mb-2">${listing.price?.toLocaleString() || 'Free'}</p>

                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <span>{listing.condition}</span>
                      <span>{listing.category}</span>
                      <span>👁️ {listing.views} views</span>
                      <span>Posted: {formatDate(listing.createdAt)}</span>
                    </div>

                    <div className="flex space-x-4">
                      <Link
                        to={`/edit-listing/${listing._id}`}
                        className="text-primary hover:underline"
                      >
                        Edit Listing
                      </Link>

                      {listing.status === 'active' ? (
                        <button
                          onClick={() => toggleListingStatus(listing._id, listing.status)}
                          className="text-orange-600 hover:underline"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleListingStatus(listing._id, listing.status)}
                          className="text-green-600 hover:underline"
                        >
                          Reactivate
                        </button>
                      )}

                      <button
                        onClick={() => deleteListing(listing._id, listing.title)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default Profile;

